import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import App from './App'
import './index.css'
import { Buffer } from 'buffer'

// Initialize Sentry FIRST (before anything else)
import { initSentry, Sentry } from './config/sentry'
initSentry()

// Config context for backend-synced settings
import { ConfigProvider } from './contexts/ConfigContext'

// Language context for i18n
import { LanguageProvider } from './contexts/LanguageContext'
import './i18n/config'

// User context for centralized user state
import { UserProvider } from './contexts/UserContext'

// Polyfill Buffer for browser
window.Buffer = Buffer

// Solana wallet adapter
import { ConnectionProvider, WalletProvider } from '@solana/wallet-adapter-react'
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui'
import { PhantomWalletAdapter } from '@solana/wallet-adapter-phantom'
import { API_CONFIG } from './config/app.config'

import '@solana/wallet-adapter-react-ui/styles.css'

// Create a React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

// Solana RPC endpoint (proxied through backend to avoid CORS/403)
const endpoint = `${API_CONFIG.baseUrl}/solana/rpc`

// Detect if user is on mobile
const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

// Configure Phantom wallet adapter with app identity (reduces "Unverified app" warning)
const wallets = [
  new PhantomWalletAdapter({
    appIdentity: {
      name: 'Takara Gold',
      url:  import.meta.env.VITE_APP_URL || 'https://takara.gold',
      icon: import.meta.env.VITE_APP_ICON_URL || 'https://takara.gold/favicon.ico',
    },
  }),
]

// Global wallet error handler
const handleWalletError = (error: Error) => {
  console.warn('Wallet adapter error:', error.name, error.message)
  // Don't throw - just log and continue
}

// Fallback component for critical errors
const ErrorFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-900">
    <div className="text-center p-8">
      <h1 className="text-2xl font-bold text-red-500 mb-4">Something went wrong</h1>
      <p className="text-gray-400 mb-4">We've been notified and are working on a fix.</p>
      <button
        onClick={() => window.location.reload()}
        className="px-6 py-3 bg-gold-500 text-black rounded-lg font-semibold hover:bg-gold-400"
      >
        Reload Page
      </button>
    </div>
  </div>
)

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog>
      <ConnectionProvider endpoint={endpoint} config={{ wsEndpoint: 'wss://api.mainnet-beta.solana.com/', disableRetryOnRateLimit: true }}>
        <WalletProvider wallets={wallets} autoConnect={false} onError={handleWalletError}>
          <WalletModalProvider>
            <QueryClientProvider client={queryClient}>
              <ConfigProvider>
                <LanguageProvider>
                  <BrowserRouter>
                    <UserProvider>
                      <App />
                    </UserProvider>
                  </BrowserRouter>
                </LanguageProvider>
              </ConfigProvider>
            </QueryClientProvider>
          </WalletModalProvider>
        </WalletProvider>
      </ConnectionProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
)
